import { useState, useMemo, useEffect } from 'react';
import Pagination from '@/components/base/Pagination';
import { useNavigate } from 'react-router-dom';
import { useFuncionarios } from '@/context/FuncionariosContext';
import type { Funcionario } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { calcularProgressao, calcularPontosCiclo, calcularLimiarFuncionario, type AvaliacaoSiadap, type PontosEscalasConfig } from '@/utils/progressaoUtils';
import { useAppSettings } from '@/context/AppSettingsContext';
import { supabase } from '@/lib/supabase';
import FuncionarioCard from './components/FuncionarioCard';
import HistoricoModal from './components/HistoricoModal';
import FuncionarioFormModal from './components/FuncionarioFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import ProgressoesTab from './components/ProgressoesTab';
import PontosManuaisModal from './components/PontosManuaisModal';
import ExportarRelatoriosModal from './components/ExportarRelatoriosModal';
import ImportarCsvModal from './components/ImportarCsvModal';
import EdicaoEmLoteModal from './components/EdicaoEmLoteModal';
import SiadapFormModal from '@/pages/siadap/components/SiadapFormModal';
import type { RegistoSiadap } from '@/context/SiadapContext';
import { MUNICIPIO_SIADAP_ID } from '@/lib/municipios';

const ESTADO_CONFIG: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  Ativo:       { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Ativo' },
  Inativo:     { bg: 'bg-zinc-100',   text: 'text-zinc-500',    dot: 'bg-zinc-400',    label: 'Inativo' },
  'Licença':   { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400',   label: 'Licença' },
  Aposentado:  { bg: 'bg-slate-100',  text: 'text-slate-500',   dot: 'bg-slate-400',   label: 'Aposentado' },
  'Cedência':  { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-400',     label: 'Cedência' },
  Mobilidade:  { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-400',  label: 'Mobilidade' },
};

// Cache global para não re-buscar o mesmo funcionário
const estadoCache: Record<string, string | null> = {};

function EstadoBadge({ funcionarioId }: { funcionarioId: string }) {
  const [estado, setEstado] = useState<string | null>(estadoCache[funcionarioId] ?? undefined as unknown as null);

  useEffect(() => {
    if (estadoCache[funcionarioId] !== undefined) {
      setEstado(estadoCache[funcionarioId]);
      return;
    }
    supabase
      .from('funcionarios')
      .select('status')
      .eq('id', funcionarioId)
      .maybeSingle()
      .then(({ data }) => {
        const val = data?.status ?? null;
        estadoCache[funcionarioId] = val;
        setEstado(val);
      });
  }, [funcionarioId]);

  if (!estado) return <span className="text-xs text-slate-300">—</span>;
  const cfg = ESTADO_CONFIG[estado] ?? { bg: 'bg-zinc-100', text: 'text-zinc-500', dot: 'bg-zinc-400', label: estado };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

type ViewMode = 'grid' | 'list';
type SortKey = 'id' | 'nome' | 'nivel' | 'pontos' | 'progressao';
type SortDir = 'asc' | 'desc';
type ActiveTab = 'funcionarios' | 'progressoes';

function calcularAnosServico(dataEntrada?: string): number | null {
  if (!dataEntrada) return null;
  const entrada = new Date(dataEntrada);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  const mesAtual = hoje.getMonth();
  const mesEntrada = entrada.getMonth();
  if (mesAtual < mesEntrada || (mesAtual === mesEntrada && hoje.getDate() < entrada.getDate())) {
    anos -= 1;
  }
  return Math.max(0, anos);
}

export default function FuncionariosPage() {
  const navigate = useNavigate();
  const { funcionarios, unidadesOrganicas, loading, addFuncionario, updateFuncionario, deleteFuncionario } = useFuncionarios();
  const { avaliacoesPorFuncionario, registosSiadap, registosHistorico, pontosDescontadosPorFuncionario, reloadSiadap } = useSiadap();
  const { settings } = useAppSettings();

  const [activeTab, setActiveTab] = useState<ActiveTab>('funcionarios');
  const [search, setSearch] = useState('');
  const [carreiraFilter, setCarreiraFilter] = useState('');
  const [sexoFilter, setSexoFilter] = useState('');
  const [unidadeFilter, setUnidadeFilter] = useState('');
  const [subunidadeFilter, setSubunidadeFilter] = useState('');
  const [avaliacaoFilter, setAvaliacaoFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSortKey(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedHistorico, setSelectedHistorico] = useState<Funcionario | null>(null);
  const [formModal, setFormModal] = useState<{ open: boolean; funcionario?: Funcionario | null }>({ open: false });
  const [deleteModal, setDeleteModal] = useState<Funcionario | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [pontosManuaisModal, setPontosManuaisModal] = useState<Funcionario | null>(null);
  const [exportarRelatoriosModal, setExportarRelatoriosModal] = useState(false);
  const [importarCsvModal, setImportarCsvModal] = useState(false);
  const [edicaoEmLoteModal, setEdicaoEmLoteModal] = useState(false);
  const [siadapModal, setSiadapModal] = useState<{ open: boolean; funcionarioId: string }>({ open: false, funcionarioId: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const carreiras = useMemo(() => Array.from(new Set(funcionarios.map((f) => f.carreira))).sort(), [funcionarios]);

  // Unidades e subunidades vindas da tabela unidades_organicas (Admin)
  const unidades = useMemo(
    () => unidadesOrganicas.filter((u) => u.tipo === 'unidade').map((u) => u.nome).sort(),
    [unidadesOrganicas]
  );
  const subunidades = useMemo(() => {
    if (!unidadeFilter) return [];
    // Encontrar o ID da unidade pai selecionada
    const paiId = unidadesOrganicas.find((u) => u.nome === unidadeFilter && u.tipo === 'unidade')?.id;
    if (!paiId) {
      // Fallback: subunidades dos funcionários com essa unidade
      return Array.from(new Set(
        funcionarios.filter((f) => f.unidadeOrganica === unidadeFilter).map((f) => f.subunidadeOrganica).filter(Boolean)
      )).sort() as string[];
    }
    return unidadesOrganicas
      .filter((u) => u.tipo === 'subunidade' && u.unidadePaiId === paiId)
      .map((u) => u.nome)
      .sort();
  }, [unidadesOrganicas, unidadeFilter, funcionarios]);

  const pontosMap = useMemo<Partial<Record<AvaliacaoSiadap, number>>>(() => ({
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  }), [settings]);

  // Escalas completas para as 3 épocas — vindas das definições de administração
  const escalas = useMemo<PontosEscalasConfig>(() => ({
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente:  settings.pontos_excelente_pre2023,
      Relevante:  settings.pontos_relevante_pre2023,
      Adequado:   settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom:         settings.pontos_adequado_pre2023,
      Regular:     settings.pontos_adequado_pre2023,
    },
  }), [settings]);

  const pontosPorFuncionario = useMemo(() => {
    const mapa: Record<string, number> = {};
    funcionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      // Usa os descontos das progressões como base, e adiciona manuais apenas se forem realmente adicionais
      const descontosProgressoes = pontosDescontadosPorFuncionario[f.id] ?? 0;
      const descontosManuais = f.pontosDescontados ?? 0;
      let pontosDescontados: number;
      if (descontosManuais > descontosProgressoes) {
        pontosDescontados = descontosManuais;
      } else if (descontosManuais === descontosProgressoes) {
        pontosDescontados = descontosProgressoes;
      } else {
        pontosDescontados = descontosProgressoes + descontosManuais;
      }
      // Exceção PREPAV: se o toggle estiver ativo, os pontos manuais são SEMPRE somados
      const isPrepav = f.excecaoPrepav === true;
      const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
      // Pontos manuais são SEMPRE somados
      const pontosManuais = f.pontosAtuais ?? 0;
      mapa[f.id] = calcularPontosCiclo(
        siadapDoFunc,
        historicoDoFunc,
        pontosDescontados,
        pontosManuais,
        pontosMap,
        undefined,
        escalas
      );
    });
    return mapa;
  }, [funcionarios, registosSiadap, registosHistorico, pontosMap, escalas, pontosDescontadosPorFuncionario]);

  const limiarPorFuncionario = useMemo(() => {
    const mapa: Record<string, number> = {};
    funcionarios.forEach((f) => {
      const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === f.id);
      const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === f.id);
      const { limiar } = calcularLimiarFuncionario(
        siadapDoFunc,
        historicoDoFunc,
        settings.pontos_progressao,
        settings.pontos_progressao_pre2025,
        settings.pontos_progressao_2023,
        escalas
      );
      mapa[f.id] = limiar;
    });
    return mapa;
  }, [funcionarios, registosSiadap, registosHistorico, settings.pontos_progressao, settings.pontos_progressao_pre2025, settings.pontos_progressao_2023, escalas]);

  const filtered = useMemo(() => {
    let list = funcionarios.filter((f) => {
      const matchSearch = search === '' || f.nome.toLowerCase().includes(search.toLowerCase()) || f.id.includes(search);
      const matchCarreira = carreiraFilter === '' || f.carreira === carreiraFilter;
      const matchSexo = sexoFilter === '' || f.sexo === sexoFilter;
      const matchUnidade = unidadeFilter === '' || f.unidadeOrganica === unidadeFilter;
      const matchSubunidade = subunidadeFilter === '' || f.subunidadeOrganica === subunidadeFilter;
      const matchAvaliacao = avaliacaoFilter === '' || f.avaliacao === avaliacaoFilter;
      return matchSearch && matchCarreira && matchSexo && matchUnidade && matchSubunidade && matchAvaliacao;
    });
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'id') cmp = (parseInt(a.id, 10) || 0) - (parseInt(b.id, 10) || 0);
      else if (sortKey === 'nome') cmp = a.nome.localeCompare(b.nome);
      else if (sortKey === 'nivel') cmp = a.nivelAtual - b.nivelAtual;
      else if (sortKey === 'pontos') cmp = (pontosPorFuncionario[a.id] ?? 0) - (pontosPorFuncionario[b.id] ?? 0);
      else if (sortKey === 'progressao') {
        const pa = calcularProgressao(pontosPorFuncionario[a.id] ?? 0, a.nivelAtual, settings.pontos_progressao).percentagem;
        const pb = calcularProgressao(pontosPorFuncionario[b.id] ?? 0, b.nivelAtual, settings.pontos_progressao).percentagem;
        cmp = pa - pb;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [funcionarios, search, carreiraFilter, sexoFilter, unidadeFilter, subunidadeFilter, avaliacaoFilter, sortKey, settings.pontos_progressao, pontosPorFuncionario]);

  const totalM = funcionarios.filter((f) => f.sexo === 'M').length;
  const totalF = funcionarios.filter((f) => f.sexo === 'F').length;
  const elegiveis = funcionarios.filter((f) => calcularProgressao(pontosPorFuncionario[f.id] ?? 0, f.nivelAtual, limiarPorFuncionario[f.id] ?? settings.pontos_progressao).status === 'pronto').length;
  const mediaVencimento = funcionarios.length > 0
    ? funcionarios.reduce((s, f) => s + (f.vencimento ?? 0), 0) / funcionarios.length
    : 0;

  const hasFilters = search || carreiraFilter || sexoFilter || unidadeFilter || subunidadeFilter || avaliacaoFilter;

  function clearFilters() {
    setSearch(''); setCarreiraFilter(''); setSexoFilter(''); setUnidadeFilter(''); setSubunidadeFilter(''); setAvaliacaoFilter('');
    setCurrentPage(1);
  }

  // Reset page when filters change
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  function showSuccess(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  }

  async function handleSave(data: Funcionario, oldId?: string) {
    setSaving(true);
    try {
      if (formModal.funcionario) {
        await updateFuncionario(data, oldId);
        showSuccess('Funcionário atualizado com sucesso.');
      } else {
        await addFuncionario(data);
        showSuccess('Funcionário adicionado com sucesso.');
        // Se foi um novo funcionário, abrir SIADAP automaticamente
        setTimeout(() => {
          setSiadapModal({ open: true, funcionarioId: data.id });
        }, 300);
      }
      setFormModal({ open: false });
    } catch (e: unknown) {
      showSuccess('Erro ao guardar: ' + (e instanceof Error ? e.message : 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSiadap(data: RegistoSiadap) {
    setSaving(true);
    try {
      const row = {
        funcionario_id: data.funcionarioId,
        periodo_avaliacao: data.periodoAvaliacao.trim(),
        avaliacao: data.avaliacao,
        pontos_chefe: data.pontosChefe,
        pontos_cca: data.pontosCCA,
        descricao_subida: data.descricaoSubida,
        nivel: data.nivel,
        remuneracao_atual: data.remuneracaoAtual,
        updated_at: new Date().toISOString(),
        municipio_id: MUNICIPIO_SIADAP_ID,
      };
      const { data: existente } = await supabase
        .from('registos_siadap')
        .select('id')
        .eq('funcionario_id', data.funcionarioId)
        .ilike('periodo_avaliacao', data.periodoAvaliacao.trim())
        .maybeSingle();
      if (existente) {
        showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
        return;
      }
      const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 's' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
      const { error } = await supabase.from('registos_siadap').insert({ id: newId, ...row });
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
          return;
        }
        throw error;
      }
      showSuccess('Avaliação SIADAP adicionada com sucesso!');
      await reloadSiadap();
      setSiadapModal({ open: false, funcionarioId: '' });
    } catch (err) {
      console.error('Erro ao guardar SIADAP:', err);
      showSuccess('Erro ao guardar avaliação. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSiadapAndContinue(data: RegistoSiadap) {
    setSaving(true);
    try {
      const row = {
        funcionario_id: data.funcionarioId,
        periodo_avaliacao: data.periodoAvaliacao.trim(),
        avaliacao: data.avaliacao,
        pontos_chefe: data.pontosChefe,
        pontos_cca: data.pontosCCA,
        descricao_subida: data.descricaoSubida,
        nivel: data.nivel,
        remuneracao_atual: data.remuneracaoAtual,
        updated_at: new Date().toISOString(),
        municipio_id: MUNICIPIO_SIADAP_ID,
      };
      const { data: existente } = await supabase
        .from('registos_siadap')
        .select('id')
        .eq('funcionario_id', data.funcionarioId)
        .ilike('periodo_avaliacao', data.periodoAvaliacao.trim())
        .maybeSingle();
      if (existente) {
        showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
        return;
      }
      const newId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 's' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
      const { error } = await supabase.from('registos_siadap').insert({ id: newId, ...row });
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          showSuccess(`Já existe uma avaliação para "${data.periodoAvaliacao}" neste funcionário.`);
          return;
        }
        throw error;
      }
      showSuccess('Avaliação adicionada! Podes continuar a inserir mais.');
      await reloadSiadap();
    } catch (err) {
      console.error('Erro ao guardar SIADAP:', err);
      showSuccess('Erro ao guardar avaliação. Tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteModal) return;
    setSaving(true);
    try {
      await deleteFuncionario(deleteModal.id);
      showSuccess('Funcionário eliminado.');
      setDeleteModal(null);
    } catch (e: unknown) {
      showSuccess('Erro ao eliminar: ' + (e instanceof Error ? e.message : 'Erro desconhecido'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <main className="flex-1 bg-slate-50/60">

        {/* ── PAGE HEADER ── */}
        <div className="bg-white border-b border-zinc-100 px-8 py-6">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
                <i className="ri-team-line text-white text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">Recursos Humanos</span>
                </div>
                <h1 className="text-[20px] font-bold text-zinc-900 tracking-tight leading-tight">Funcionários</h1>
              </div>
            </div>
            {activeTab === 'funcionarios' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setImportarCsvModal(true)}
                  className="whitespace-nowrap flex items-center gap-2 border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></div>
                  Importar CSV
                </button>
                <button
                  onClick={() => setExportarRelatoriosModal(true)}
                  className="whitespace-nowrap flex items-center gap-2 border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-chart-2-line text-sm" /></div>
                  Exportar Relatórios
                </button>
                <button
                  onClick={() => setEdicaoEmLoteModal(true)}
                  className="whitespace-nowrap flex items-center gap-2 border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-edit-2-line text-sm" /></div>
                  Edição em Lote
                </button>
                <button
                  onClick={() => setFormModal({ open: true, funcionario: null })}
                  className="whitespace-nowrap flex items-center gap-2 bg-zinc-900 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm" /></div>
                  Novo Funcionário
                </button>
              </div>
            )}
          </div>

          {/* ── TABS ── */}
          <div className="flex items-center gap-1 bg-zinc-100 rounded-xl p-1 w-fit">
            {([
              { key: 'funcionarios', label: 'Funcionários', icon: 'ri-team-line', count: funcionarios.length },
              { key: 'progressoes', label: 'Progressões', icon: 'ri-arrow-up-circle-line', count: null },
            ] as { key: ActiveTab; label: string; icon: string; count: number | null }[]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all cursor-pointer ${
                  activeTab === tab.key ? 'bg-white text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`} />
                </div>
                {tab.label}
                {tab.count !== null && (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                    activeTab === tab.key ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-200 text-zinc-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="px-8 py-6 space-y-6">

          {/* ── TOAST ── */}
          {successMsg && (
            <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-lg">
              <div className="w-5 h-5 flex items-center justify-center"><i className="ri-checkbox-circle-line text-base" /></div>
              {successMsg}
            </div>
          )}

          {/* ── TAB: PROGRESSÕES ── */}
          {activeTab === 'progressoes' && <ProgressoesTab />}

          {/* ── TAB: FUNCIONÁRIOS ── */}
          {activeTab === 'funcionarios' && (
            <>
              {/* STATS */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-team-line',            value: funcionarios.length, label: 'Total de Funcionários',    sub: `${totalM} masc. · ${totalF} fem.`, iconBg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-briefcase-line',        value: carreiras.length,    label: 'Carreiras Distintas',      sub: carreiras.slice(0,3).join(' · ') + (carreiras.length > 3 ? '…' : ''), iconBg: 'bg-amber-50 text-amber-600' },
                  { icon: 'ri-arrow-up-circle-line',  value: elegiveis,           label: 'Elegíveis para Progressão',sub: `${funcionarios.length > 0 ? ((elegiveis / funcionarios.length) * 100).toFixed(0) : 0}% do total`, iconBg: 'bg-emerald-50 text-emerald-600' },
                  { icon: 'ri-money-euro-circle-line',value: mediaVencimento.toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }), label: 'Vencimento Médio', sub: 'Média da equipa', iconBg: 'bg-rose-50 text-rose-500' },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${s.iconBg}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[22px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-medium text-zinc-600 mt-0.5">{s.label}</p>
                      <p className="text-[11px] text-zinc-400 truncate">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* FILTROS */}
              <div className="bg-white rounded-xl border border-zinc-100 p-4 space-y-3">
                <div className="flex gap-2.5 items-center">
                  <div className="relative flex-1">
                    <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                      <i className="ri-search-line text-sm" />
                    </div>
                    <input
                      type="text"
                      placeholder="Pesquisar por nome ou nº de funcionário..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1 flex-shrink-0">
                    <button onClick={() => setViewMode('grid')} className={`whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'grid' ? 'bg-white text-zinc-700' : 'text-zinc-400 hover:text-zinc-600'}`}>
                      <i className="ri-grid-line text-sm" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${viewMode === 'list' ? 'bg-white text-zinc-700' : 'text-zinc-400 hover:text-zinc-600'}`}>
                      <i className="ri-list-check text-sm" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-[11px] font-semibold text-zinc-400 flex-shrink-0">Unidade:</span>
                  <select value={unidadeFilter} onChange={(e) => { setUnidadeFilter(e.target.value); setSubunidadeFilter(''); }}
                    className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${unidadeFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                    <option value="">Todas as Unidades</option>
                    {unidades.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {unidadeFilter && subunidades.length > 0 && (
                    <select value={subunidadeFilter} onChange={(e) => setSubunidadeFilter(e.target.value)}
                      className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${subunidadeFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                      <option value="">Todas as Subunidades</option>
                      {subunidades.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-zinc-100">
                  <select value={carreiraFilter} onChange={(e) => setCarreiraFilter(e.target.value)} className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${carreiraFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                    <option value="">Todas as Carreiras</option>
                    {carreiras.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={avaliacaoFilter} onChange={(e) => setAvaliacaoFilter(e.target.value)} className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${avaliacaoFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                    <option value="">Todas as Avaliações</option>
                    {['Excelente', 'Muito Bom', 'Adequado', 'Inadequado'].map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <select value={sexoFilter} onChange={(e) => setSexoFilter(e.target.value)} className={`text-[13px] border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer ${sexoFilter ? 'border-zinc-800 text-zinc-800 font-medium' : 'border-zinc-200 text-zinc-600'}`}>
                    <option value="">Todos os Géneros</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                  <div className="flex items-center gap-1.5 ml-auto">
                    {([{ key: 'id', label: 'Nº' }, { key: 'nome', label: 'Nome' }, { key: 'nivel', label: 'Nível' }, { key: 'pontos', label: 'Pontos' }, { key: 'progressao', label: 'Progressão' }] as { key: SortKey; label: string }[]).map((s) => (
                      <button
                        key={s.key}
                        onClick={() => handleSortKey(s.key)}
                        className={`whitespace-nowrap flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                          sortKey === s.key ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                        }`}
                      >
                        {s.label}
                        {sortKey === s.key && (
                          <i className={`${
                            sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'
                          } text-[13px]`} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {hasFilters && <span className="text-[11px] text-zinc-400">Filtros ativos:</span>}
                    {search && <span className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">"{search}"</span>}
                    {unidadeFilter && <span className="text-[11px] bg-zinc-900 text-white px-2 py-0.5 rounded-full">{unidadeFilter}</span>}
                    {carreiraFilter && <span className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{carreiraFilter}</span>}
                    {avaliacaoFilter && <span className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{avaliacaoFilter}</span>}
                    {sexoFilter && <span className="text-[11px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">{sexoFilter === 'M' ? 'Masculino' : 'Feminino'}</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    {hasFilters && (
                      <button onClick={clearFilters} className="whitespace-nowrap flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">
                        <i className="ri-close-circle-line text-sm" />Limpar
                      </button>
                    )}
                    <span className="text-[12px] text-zinc-400"><span className="font-semibold text-zinc-600">{filtered.length}</span> funcionário(s)</span>
                  </div>
                </div>
              </div>

              {/* LEGENDA */}
              <div className="flex items-center gap-4 flex-wrap">
                <span className="text-[11px] text-zinc-400 font-medium">Progressão:</span>
                {[{ color: 'bg-emerald-500', label: 'Elegível' }, { color: 'bg-amber-400', label: 'Próximo' }, { color: 'bg-zinc-300', label: 'Em progresso' }].map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${l.color}`} />
                    <span className="text-[11px] text-zinc-500">{l.label}</span>
                  </div>
                ))}
              </div>

              {/* CONTENT */}
              {loading ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-24 flex flex-col items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
                  <p className="text-[13px] text-zinc-400">A carregar funcionários...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-zinc-100 py-24 flex flex-col items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center"><i className="ri-search-line text-4xl text-zinc-200" /></div>
                  <p className="text-[13px] font-medium text-zinc-400">Nenhum funcionário encontrado</p>
                  {hasFilters && <button onClick={clearFilters} className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 underline cursor-pointer">Limpar filtros</button>}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {paginated.map((f, i) => (
                    <div key={f.id} className="relative group">
                      <FuncionarioCard
                        funcionario={f}
                        index={i}
                        onClick={() => setSelectedHistorico(f)}
                        avaliacoesCount={avaliacoesPorFuncionario[f.id] ?? 0}
                        pontosCalculados={pontosPorFuncionario[f.id]}
                        limiar={limiarPorFuncionario[f.id]}
                      />
                      <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${f.id}`); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-zinc-800 hover:border-zinc-400 transition-colors cursor-pointer" title="Ver ficha completa">
                          <i className="ri-eye-line text-sm" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setPontosManuaisModal(f); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-amber-600 hover:border-amber-300 transition-colors cursor-pointer" title="Ajustar pontos manualmente">
                          <i className="ri-coins-line text-sm" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setFormModal({ open: true, funcionario: f }); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-slate-800 hover:border-slate-400 transition-colors cursor-pointer" title="Editar">
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteModal(f); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center bg-white border border-slate-200 rounded-lg text-slate-500 hover:text-red-500 hover:border-red-300 transition-colors cursor-pointer" title="Eliminar">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        {([
                          { key: 'id' as SortKey, label: 'Funcionário' },
                          { key: null, label: 'Carreira / Unidade' },
                          { key: null, label: 'Estado' },
                          { key: null, label: 'Avaliação' },
                          { key: 'progressao' as SortKey, label: 'Progressão' },
                          { key: null, label: 'Anos Serviço' },
                          { key: 'nivel' as SortKey, label: 'Nível' },
                          { key: null, label: 'Vencimento' },
                          { key: null, label: 'Ações', align: 'right' },
                        ] as { key: SortKey | null; label: string; align?: string }[]).map((col, ci) => (
                          <th
                            key={ci}
                            onClick={() => col.key && handleSortKey(col.key)}
                            className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap select-none group ${
                              col.key ? 'cursor-pointer hover:bg-zinc-100 transition-colors' : ''
                            } ${
                              sortKey === col.key ? 'text-slate-700' : 'text-slate-400'
                            } ${col.align === 'right' ? 'text-right' : ''}`}
                          >
                            <span className="inline-flex items-center gap-1">
                              {col.label}
                              {col.key && (
                                sortKey === col.key
                                  ? <i className={`${
                                      sortDir === 'asc' ? 'ri-arrow-up-s-fill' : 'ri-arrow-down-s-fill'
                                    } text-[13px] text-slate-700`} />
                                  : <i className="ri-expand-up-down-line text-[11px] text-slate-300 group-hover:text-slate-400" />
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((f, i) => {
                        const avalCfg: Record<string, { bg: string; text: string; dot: string }> = {
                          Excelente: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
                          'Muito Bom': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
                          Adequado: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
                          Inadequado: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
                        };
                        const aval = avalCfg[f.avaliacao] ?? avalCfg['Adequado'];
                        const initials = f.nome.split(' ').filter((_, idx, arr) => idx === 0 || idx === arr.length - 1).map((n) => n[0]).join('');
                        const avatarColors = ['bg-rose-100 text-rose-700','bg-violet-100 text-violet-700','bg-amber-100 text-amber-700','bg-teal-100 text-teal-700','bg-sky-100 text-sky-700','bg-orange-100 text-orange-700'];
                        const colorClass = avatarColors[i % avatarColors.length];
                        return (
                          <tr key={f.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer group" onClick={() => setSelectedHistorico(f)}>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${colorClass}`}>{initials}</div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{f.nome}</p>
                                  <p className="text-xs text-slate-400 font-mono">#{f.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              <p className="text-xs font-medium text-slate-700">{f.carreira}</p>
                              <p className="text-xs text-slate-400 truncate max-w-[180px]">{f.unidadeOrganica}</p>
                            </td>
                            <td className="px-5 py-3">
                              <EstadoBadge funcionarioId={f.id} />
                            </td>
                            <td className="px-5 py-3">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${aval.bg} ${aval.text}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${aval.dot}`} />
                                {f.avaliacao}
                              </span>
                            </td>
                            <td className="px-5 py-3">
                              <div className="space-y-1">
                                {(() => {
                                  const pontos = pontosPorFuncionario[f.id] ?? 0;
                                  const prog = calcularProgressao(pontos, f.nivelAtual, limiarPorFuncionario[f.id] ?? settings.pontos_progressao);
                                  const sc = getStatusColor(prog.status);
                                  return (
                                    <>
                                      <div className="flex items-center justify-between">
                                        <span className={`text-xs font-medium ${sc.text}`}>{prog.status === 'pronto' ? 'Elegível' : `${pontos}/${prog.pontosNecessarios} pts`}</span>
                                        <span className="text-xs text-slate-400">{prog.percentagem.toFixed(0)}%</span>
                                      </div>
                                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden w-36">
                                        <div className={`h-full rounded-full ${sc.bar}`} style={{ width: `${prog.percentagem}%` }} />
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              {(() => {
                                const anos = calcularAnosServico(f.dataEntradaFuncaoPublica);
                                if (anos === null) return <span className="text-xs text-slate-300">—</span>;
                                return (
                                  <div className="flex items-center gap-1.5">
                                    <div className="w-5 h-5 flex items-center justify-center text-slate-300">
                                      <i className="ri-time-line text-sm" />
                                    </div>
                                    <span className="text-sm font-semibold text-slate-700">{anos}</span>
                                    <span className="text-xs text-slate-400">anos</span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-5 py-3">
                              <span className="text-sm font-semibold text-slate-700">{(f.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}</span>
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={(e) => { e.stopPropagation(); navigate(`/funcionarios/${f.id}`); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-zinc-800 hover:bg-zinc-100 transition-colors cursor-pointer" title="Ver ficha completa">
                                  <i className="ri-eye-line text-sm" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setPontosManuaisModal(f); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer" title="Ajustar pontos manualmente">
                                  <i className="ri-coins-line text-sm" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setFormModal({ open: true, funcionario: f }); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer" title="Editar">
                                  <i className="ri-edit-line text-sm" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); setDeleteModal(f); }} className="whitespace-nowrap w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Eliminar">
                                  <i className="ri-delete-bin-line text-sm" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && filtered.length > 0 && (
                <Pagination
                  currentPage={safePage}
                  totalItems={filtered.length}
                  pageSize={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  itemLabel="funcionário(s)"
                />
              )}

              {!loading && filtered.length > 0 && (
                <div className="flex items-center justify-between text-xs text-slate-400 pb-2">
                  <span>SIADAP para RH · Câmara Municipal</span>
                  <span>Atualizado em {new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {selectedHistorico && <HistoricoModal funcionario={selectedHistorico} onClose={() => setSelectedHistorico(null)} />}
      {pontosManuaisModal && (
        <PontosManuaisModal
          funcionario={pontosManuaisModal}
          pontosCalculados={pontosPorFuncionario[pontosManuaisModal.id] ?? pontosManuaisModal.pontosAtuais ?? 0}
          onClose={() => setPontosManuaisModal(null)}
          onSuccess={() => { setPontosManuaisModal(null); showSuccess('Pontos ajustados com sucesso!'); }}
          isPrepav={pontosManuaisModal.excecaoPrepav === true}
        />
      )}
      {formModal.open && (
        <FuncionarioFormModal funcionario={formModal.funcionario} onSave={handleSave} onClose={() => setFormModal({ open: false })} />
      )}
      {deleteModal && (
        <DeleteConfirmModal funcionario={deleteModal} onConfirm={handleDelete} onClose={() => setDeleteModal(null)} />
      )}
      {exportarRelatoriosModal && (
        <ExportarRelatoriosModal onClose={() => setExportarRelatoriosModal(false)} />
      )}
      {importarCsvModal && (
        <ImportarCsvModal
          onClose={() => setImportarCsvModal(false)}
          onSuccess={(count) => {
            setImportarCsvModal(false);
            showSuccess(`${count} funcionário(s) importados com sucesso!`);
          }}
        />
      )}
      {edicaoEmLoteModal && (
        <EdicaoEmLoteModal
          onClose={() => setEdicaoEmLoteModal(false)}
          onSuccess={(count) => {
            setEdicaoEmLoteModal(false);
            showSuccess(`${count} funcionário(s) atualizados com sucesso!`);
          }}
        />
      )}
      {siadapModal.open && (
        <SiadapFormModal
          registo={null}
          funcionarioId={siadapModal.funcionarioId}
          onSave={handleSaveSiadap}
          onSaveAndContinue={handleSaveSiadapAndContinue}
          onClose={() => setSiadapModal({ open: false, funcionarioId: '' })}
        />
      )}
      {saving && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-xl px-6 py-4 flex items-center gap-3 text-slate-700 text-sm">
            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-loader-4-line animate-spin text-lg" /></div>
            A guardar...
          </div>
        </div>
      )}
    </>
  );
}

function getStatusColor(status: 'pronto' | 'proximo' | 'em_progresso') {
  switch (status) {
    case 'pronto': return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' };
    case 'proximo': return { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' };
    default: return { bar: 'bg-slate-300', text: 'text-slate-500', bg: 'bg-slate-50', badge: 'bg-slate-100 text-slate-600' };
  }
}